import { useEffect, useState, useCallback } from "react";

global.appVersion = process.env.REACT_APP_VERSION;

// Records the commit a reload was already attempted for. A mismatch the reload
// cannot clear — a CDN still serving the old bundle, a cached index.html —
// would otherwise reload on every load indefinitely.
const RELOAD_GUARD_KEY = "cacheBusterReloadedFor";

const readReloadGuard = () => {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch (e) {
    return null;
  }
};

const writeReloadGuard = commitVersion => {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, commitVersion);
  } catch (e) {
    // Storage can be unavailable in private browsing. The reload still happens;
    // it just isn't remembered.
  }
};

const CacheBuster = ({ children }) => {
  const [loading, setLoading] = useState(true);
  // Assume current until a successful check says otherwise. Defaulting to stale
  // means any failure to answer the question is read as "out of date".
  const [isLatestVersion, setIsLatestVersion] = useState(true);

  const refreshCacheAndReload = useCallback(async () => {
    // The service worker serves index.html from its own precache, so clearing
    // CacheStorage alone leaves the old bundle in place and the reload lands on
    // the same version.
    if ("serviceWorker" in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
      } catch (e) {
        console.error("Failed to unregister service workers", e);
      }
    }

    if (typeof caches !== "undefined") {
      try {
        const names = await caches.keys();
        await Promise.all(names.map(name => caches.delete(name)));
      } catch (e) {
        console.error("Failed to clear caches", e);
      }
    }

    // reload(true) has no effect — the forceReload argument was dropped from the
    // spec and is ignored. Unregistering the worker above is what makes the
    // reload fetch fresh assets.
    window.location.reload();
  }, []);

  const cacheJudge = useCallback(async () => {
    try {
      const response = await fetch("/meta.json", { cache: "no-store" });
      // An error page is often HTML, which would otherwise surface further down
      // as a JSON parse failure.
      if (!response.ok) {
        throw new Error(`meta.json responded ${response.status}`);
      }

      const meta = await response.json();
      const isStale =
        meta.commitVersion !== global.appVersion &&
        window.location.hostname !== "localhost";

      setIsLatestVersion(!isStale);
      setLoading(false);

      if (isStale && readReloadGuard() !== meta.commitVersion) {
        writeReloadGuard(meta.commitVersion);
        refreshCacheAndReload();
      }
      // A mismatch that survives its reload falls through to the banner, which
      // lets the user retry deliberately.
    } catch (e) {
      // A failed check says nothing about how old the bundle is. Treating it as
      // stale would reload, fail the same way, and reload again.
      console.error("Cache judge failed", e);
      setIsLatestVersion(true);
      setLoading(false);
    }
  }, [refreshCacheAndReload]);

  useEffect(() => {
    cacheJudge();
    const interval = setInterval(cacheJudge, 600000);
    return () => clearInterval(interval);
  }, [cacheJudge]);

  return children({ loading, isLatestVersion, refreshCacheAndReload });
};

export default CacheBuster;
